import { InfoOutlined } from '@mui/icons-material';
import TripAirplane from '../../../../components/icons/TripAirplane';
import { Box, Card, Divider, Typography } from '@mui/material';

const TripCriteriaCard = ({ card, isQualified }) => (
    <Box sx={{ position: 'relative', flex: 1, minWidth: 0, p: '3px' }}>
        {/* Inner card — overflow:hidden clips image/header to rounded corners */}
        <Box
            sx={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                bgcolor: 'white',
                ...(isQualified && {
                    animation: 'borderPulse 2.4s ease-in-out infinite',
                    '@keyframes borderPulse': {
                        '0%, 100%': { boxShadow: '0 0 0 1.5px rgba(46,139,46,0.6), 0 0 6px rgba(0,128,0,0.2)' },
                        '50%':      { boxShadow: '0 0 0 2.5px rgba(46,139,46,1),   0 0 14px rgba(0,128,0,0.5)' },
                    },
                }),
            }}
        >
            {/* White spotlight glow — stays inside inner card, top-left corner */}
            {isQualified && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        zIndex: 3,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        borderRadius: '16px',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -21,
                            left: -22,
                            width: 102,
                            height: 72,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: '-70% -50%',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.4) 35%, transparent 65%)',
                                animation: 'tripGlow 2.4s ease-in-out infinite',
                            },
                            '@keyframes tripGlow': {
                                '0%, 100%': { opacity: 0.5, transform: 'scale(0.9)' },
                                '50%': { opacity: 1, transform: 'scale(1.1)' },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Teal header — natural in-flow, bgcolor on card.name container */}
            {/* pt:1(8px) + h2 lineHeight(24px) = 32px, matching Figma's h-[32px] teal bar */}
            <Box sx={{ bgcolor: '#b1d3db', borderRadius: '16px 16px 0 0', px: 1, pt: 1, pb: 0, position: 'relative', zIndex: 3 }}>
                <Typography variant="h2" sx={{ color: 'white' }}>
                    {card.name}
                </Typography>
            </Box>

            {/* Content below header — normal flow */}
            <Box sx={{ px: 1, pt: '4px', position: 'relative', zIndex: 3 }}>
                <Typography variant="labelM">
                    {card.title}
                </Typography>
                <Typography variant="p3" color='#333333'>
                    {card.subtitle}
                </Typography>
                <Typography variant="p3" color='#666666'>
                    {card.description}
                </Typography>
            </Box>

            {/* Destination image — rounded top corners, fills bottom */}
            {card.image_url && (
                <Box
                    component="img"
                    src={card.image_url}
                    alt={card.name}
                    sx={{
                        position: 'relative',
                        zIndex: 3,
                        width: '100%',
                        height: 55,
                        objectFit: 'cover',
                        display: 'block',
                        borderRadius: '8px 8px 0 0',
                        mt: '6px',
                    }}
                />
            )}

            {/* Left notch */}
            <Box sx={{ position: 'absolute', left: -5, top: 52, width: 10, height: 10, borderRadius: '50%', bgcolor: '#e8f0f2', zIndex: 4 }} />
            {/* Right notch */}
            <Box sx={{ position: 'absolute', right: -5, top: 52, width: 10, height: 10, borderRadius: '50%', bgcolor: '#e8f0f2', zIndex: 4 }} />
        </Box>

    </Box>
);

const getPct = (value, maxValue) =>
    maxValue > 0 ? Math.min(100, Math.max(0, (value / maxValue) * 100)) : 0;

const SalesGrowthCard = ({ sales_info, onTncClick }) => {
    const slider_info = sales_info?.slider_info;
    const categories = slider_info?.categories || [];
    const currentValue = slider_info?.current_value || 0;
    const maxValue = categories.length ? categories[categories.length - 1].value : 0;
    const barPct = getPct(currentValue, maxValue);

    // Tooltip clamps: keep text visible near edges
    const tooltipAlign = barPct < 15
        ? { left: 0, transform: 'none' }
        : barPct > 85
            ? { right: 0, left: 'auto', transform: 'none' }
            : { left: '50%', transform: 'translateX(-50%)' };

    // Knob centering: snap to edges to avoid overflow
    const knobTransform = barPct === 0
        ? 'translateX(0%) translateY(-50%)'
        : barPct >= 100
            ? 'translateX(-100%) translateY(-50%)'
            : 'translateX(-50%) translateY(-50%)';

    return (
        <Card sx={{ backgroundColor: '#00000000', boxShadow: 'none', overflow: 'visible' }}>
            {/* Header row */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h2">
                    {sales_info?.header_info?.display_label}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h3">Period:</Typography>
                    <Typography variant="body1">{sales_info?.header_info?.value}</Typography>
                </Box>
            </Box>

            {/* Progress bar + markers */}
            <Box sx={{ position: 'relative', mt: 8, mb: 1 }}>
                {/* Track */}
                <Box sx={{ height: 8, borderRadius: '100px', bgcolor: '#D9D9D9', position: 'relative', overflow: 'visible' }}>
                    {/* Gradient fill */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${barPct}%`,
                            borderRadius: '100px',
                            background: 'linear-gradient(90deg, #b1d3db 0%, #008000 100%)',
                        }}
                    />
                    {/* Knob + tooltip */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: `${barPct}%`,
                            transform: knobTransform,
                            zIndex: 2,
                        }}
                    >
                        {/* Tooltip */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 'calc(100% + 6px)',
                                bgcolor: '#995A00',
                                color: 'white',
                                borderRadius: '4px',
                                px: '6px',
                                py: '2px',
                                whiteSpace: 'nowrap',
                                fontSize: 11,
                                lineHeight: '16px',
                                zIndex: 3,
                                ...tooltipAlign,
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '100%',
                                    left: barPct < 15 ? '8px' : barPct > 85 ? 'auto' : '50%',
                                    right: barPct > 85 ? '8px' : 'auto',
                                    transform: barPct >= 15 && barPct <= 85 ? 'translateX(-50%)' : 'none',
                                    border: '4px solid transparent',
                                    borderTopColor: '#995A00',
                                },
                            }}
                        >
                            {currentValue} {slider_info?.uom}*
                        </Box>
                        {/* Airplane icon */}
                        <TripAirplane width={32} height={37} style={{ transform: 'rotate(50.65deg)', display: 'block' }} />
                    </Box>
                </Box>

                {/* Category markers — absolutely positioned by value */}
                {categories.length > 0 && (
                    <Box sx={{ position: 'relative', height: 28, mt: 0.5 }}>
                        {categories.map((cat) => {
                            const pct = getPct(cat.value, maxValue);
                            const align =
                                pct <= 5  ? { left: 0, transform: 'none' } :
                                pct >= 95 ? { right: 0, left: 'auto', transform: 'none' } :
                                            { left: `${pct}%`, transform: 'translateX(-50%)' };
                            return (
                                <Box
                                    key={cat.display_label}
                                    sx={{ position: 'absolute', top: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', ...align }}
                                >
                                    <Box sx={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '10px solid #995A00' }} />
                                    <Typography variant="labelM" sx={{ color: '#333' }}>{cat.display_label}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Next category info */}
            {slider_info?.next_category_info && (
                <>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
                            <InfoOutlined sx={{ fontSize: 16, color: '#333', flexShrink: 0 }} />
                            <Typography variant="labelM">
                                {slider_info.next_category_info.display_label}
                            </Typography>
                        </Box>
                        <Typography variant="p3" sx={{ color: '#333', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {slider_info.next_category_info.value}
                        </Typography>
                    </Box>
                </>
            )}

            {/* Trip criteria cards */}
            {sales_info?.category_cards_info?.length > 0 && (
                <Box sx={{ display: 'flex', gap: .5, mt: 2, pb: 0.5 }}>
                    {(() => {
                        const qualifiedLabel = categories.reduce(
                            (highest, cat) => currentValue >= cat.value ? cat.display_label : highest,
                            null
                        );
                        return sales_info.category_cards_info.map((card, index) => {
                            const isQualified = card.name === qualifiedLabel;
                            return <TripCriteriaCard key={index} card={card} isQualified={isQualified} />;
                        });
                    })()}
                </Box>
            )}

            {/* Footnote */}
            {sales_info?.note && (
                <Typography variant="p3" sx={{ color: '#8c8c8c', mt: 1.5, display: 'block' }}>
                    {sales_info.note.replace('T&C Applied', '')}
                    <Box
                        component="span"
                        onClick={onTncClick}
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        T&amp;C Applied
                    </Box>
                </Typography>
            )}
        </Card>
    );
};

export default SalesGrowthCard;
